import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { UpdateEmployeeTimeOffStockCommand } from 'axiosApi/models/update-employee-time-off-stock-command';
import { EmployeeTimeOffRegimeDetailDto } from 'axiosApi/models/employee-time-off-regime-detail-dto';
import Modal from 'components/common/Modal';
import { handleAPIError } from 'common/errorHandler';
import ErrorSummary from 'components/common/ErrorSummary';

interface EditTimeOffStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  stock: any | null;
  employeeRegimeId: number;
  employeeTimeOffData: EmployeeTimeOffRegimeDetailDto | null;
  onSuccess?: () => void;
}

interface FormData {
  validFrom: Date | null;
  validUntil: Date | null;
  totalStock: number;
}

const normalizeDateOnlyForApi = (date: Date | null): Date | undefined => {
  if (!date) return undefined;
  const normalized = new Date(date);
  normalized.setHours(12, 0, 0, 0);
  return normalized;
};

const exceedsOneYearRange = (start: Date | null, end: Date | null): boolean => {
  if (!start || !end) return false;
  const maxEndDate = new Date(start);
  maxEndDate.setFullYear(maxEndDate.getFullYear() + 1);
  return end >= maxEndDate;
};

const EditTimeOffStockModal: React.FC<EditTimeOffStockModalProps> = ({
  isOpen,
  onClose,
  stock,
  employeeRegimeId,
  employeeTimeOffData,
  onSuccess
}) => {
  const api = useApi();
  const toast = useToastMessageQueue();

  const [formData, setFormData] = useState<FormData>({
    validFrom: null,
    validUntil: null,
    totalStock: 0
  });

  const [originalFormData, setOriginalFormData] = useState<FormData>({
    validFrom: null,
    validUntil: null,
    totalStock: 0
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (stock) {
      const newFormData = {
        validFrom: stock.validFrom ? new Date(stock.validFrom) : null,
        validUntil: stock.validUntil ? new Date(stock.validUntil) : null,
        totalStock: stock.totalStock || 0
      };
      setFormData(newFormData);
      setOriginalFormData(newFormData);
      setErrors({});
    }
  }, [stock]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.validFrom) {
      newErrors.validFrom = intl.get('validate.errors.isRequired');
    }

    if (!formData.validUntil) {
      newErrors.validUntil = intl.get('validate.errors.isRequired');
    }

    if (formData.validFrom && formData.validUntil && formData.validFrom > formData.validUntil) {
      newErrors.validUntil = intl.get('validation.dateRange.endAfterStart');
    } else if (exceedsOneYearRange(formData.validFrom, formData.validUntil)) {
      newErrors.validUntil = intl.get('validation.dateRange.oneYearMax');
      newErrors.validFrom = intl.get('validation.dateRange.oneYearMax');
    }

    if (formData.totalStock < 0) {
      newErrors.totalStock = intl.get('validation.number.positive');
    } else if (formData.totalStock < stock.availableStock) {
      newErrors.totalStock = intl.get('timeOff.newRegimeType.errors.defaultDaysExceedAvailable');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!stock || !api || !validateForm()) return;

    setIsSubmitting(true);
    try {
      const updateCommand: UpdateEmployeeTimeOffStockCommand = {
        id: stock.id,
        idEmployeeTimeOffRegime: employeeRegimeId,
        idEmployeeRegimeTimeOffType: stock.idEmployeeRegimeTimeOffType,
        validFrom: normalizeDateOnlyForApi(formData.validFrom),
        validUntil: normalizeDateOnlyForApi(formData.validUntil),
        totalStock: formData.totalStock,
        userEntityId: undefined
      };

      const response = await api.timeoffApi.apiVversionEmployeeTimeOffStockPut("1", updateCommand);

      if (response.status === 200) {
        toast.success({ message: intl.get('timeOff.messages.updateSuccess') });
        onSuccess?.();
        handleClose();
      } else {
        toast.error({ message: intl.get('timeOff.messages.updateError') });
      }
    } catch (error: any) {
      toast.error({
        message: intl.get(error.response?.data?.errors[0]?.errorLabelKey) 
      });
      handleAPIError(error.response.data.errors[0], toast, errors);
      setErrors({ ...errors });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      validFrom: null,
      validUntil: null,
      totalStock: 0
    });
    setOriginalFormData({
      validFrom: null,
      validUntil: null,
      totalStock: 0
    });
    setErrors({});
    onClose();
  };

  const getTimeOffTypeName = () => {
    return stock.name || intl.get('timeOff.common.unknown');
  };

  const getAvailableDays = () => {
    return stock?.availableStock || 0;
  };

  const getPerks = () => {
    const perks: string[] = [];

    if (!stock || !employeeTimeOffData) {
      return perks;
    }

    const regimeType = employeeTimeOffData.employeeRegimeTimeOffTypes?.find(
      rt => rt.id === stock.idEmployeeRegimeTimeOffType
    );

    const timeOffType = regimeType?.timeOffType;

    if (timeOffType?.isBillable) {
      perks.push(intl.get('timeOff.perks.billable'));
    }
    if (timeOffType?.isCumulative) {
      perks.push(intl.get('timeOff.perks.cumulative'));
    }
    if (timeOffType?.requiresSpecialApproval) {
      perks.push(intl.get('timeOff.perks.specialApproval'));
    }
    if (timeOffType?.allowNegativeBalance) {
      perks.push(intl.get('timeOff.perks.negativeBalance'));
    }
    return perks;
  };

  const getPerkChipColor = (perk: string) => {
    switch (perk) {
      case intl.get('timeOff.perks.billable'):
        return 'primary';
      case intl.get('timeOff.perks.cumulative'):
        return 'secondary';
      case intl.get('timeOff.perks.specialApproval'):
        return 'warning';
      case intl.get('timeOff.perks.negativeBalance'):
        return 'error';
      default:
        return 'default';
    }
  };

  const changesMade =
    formData.validFrom?.getTime() !== originalFormData.validFrom?.getTime() ||
    formData.validUntil?.getTime() !== originalFormData.validUntil?.getTime() ||
    formData.totalStock !== originalFormData.totalStock;

  if (!stock) return null;

  return (
    <Modal isOpen={isOpen} closeModal={handleClose} size="medium">
      <ErrorSummary errors={errors} showEverything={true} mode="warning" />
      <Box sx={{ position: 'relative', pt: 2 }}>
        <Box sx={{ mb: 3, pr: 5 }}>
          <Typography variant="h5" component="h2" fontWeight="bold">
            {intl.get('timeOff.edit.title')}
          </Typography>
        </Box>

        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {intl.get('timeOff.table.name')}
              </Typography>
              <Typography variant="body1" sx={{ py: 1 }}>
                {getTimeOffTypeName()}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {intl.get('timeOff.table.availableDays')}
              </Typography>
              <Typography variant="body1" sx={{ py: 1 }}>
                {getAvailableDays()}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              {getPerks().length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {intl.get('timeOff.table.perks')}
                  </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ py: 1 }}>
                  {getPerks().map((perk, index) => (
                    <Chip
                      key={index}
                      label={perk}
                      size="small"
                      color={getPerkChipColor(perk) as any}
                      variant="outlined"
                    />
                  ))}
                </Box>
                </>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {intl.get('timeOff.table.validity')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box display="flex" gap={2}>
                  <DatePicker
                    label={intl.get('timeOff.assignment.dateFrom')}
                    value={formData.validFrom}
                    onChange={(newValue) => setFormData(prev => ({ ...prev, validFrom: newValue }))}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.validFrom,
                        helperText: !exceedsOneYearRange(formData.validFrom, formData.validUntil) && errors.validFrom,
                        size: 'small'
                      }
                    }}
                  />
                  <DatePicker
                    label={intl.get('timeOff.assignment.dateUntil')}
                    value={formData.validUntil}
                    onChange={(newValue) => setFormData(prev => ({ ...prev, validUntil: newValue }))}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.validUntil,
                        helperText: !exceedsOneYearRange(formData.validFrom, formData.validUntil) && errors.validUntil,
                        size: 'small'
                      }
                    }}
                  />
                </Box>
              </LocalizationProvider>
              {!!errors.validFrom && exceedsOneYearRange(formData.validFrom, formData.validUntil) && (
                <Typography variant="caption" color="error">
                  {intl.get('validation.dateRange.oneYearMax')}
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {intl.get('timeOff.table.annualAssignment')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={formData.totalStock == 0 ? '' : formData.totalStock}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  totalStock: parseInt(e.target.value) || 0
                }))}
                error={!!errors.totalStock}
                helperText={errors.totalStock}
                size="small"
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {intl.get('common.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={isSubmitting || !changesMade}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
            >
              {intl.get('common.confirm')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditTimeOffStockModal;
